<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-text-field
          v-model.trim="form.usn_id"
          label="USN"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-text-field
          v-model.trim="form.application_id"
          label="Application ID"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-select
          v-model="remainingFilter"
          :items="remainingOptions"
          item-text="text"
          item-value="value"
          outlined
          dense
          hide-details="auto"
          label="Status"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          :disabled="loading"
          @click="fetchExcessPayments"
        >
          Search
        </v-btn>
        <v-btn
          class="accent white--text ml-2"
          :loading="downloadLoading"
          :disabled="downloadLoading"
          @click="downloadExcessReport()"
        >
          Download
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="groupHeaders"
            :items="filteredGroups"
            :loading="loading"
            fixed-header
            item-key="student_key"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>

            <template #[`item.usn_id`]="{ item }">
              {{ item.usn_id || "—" }}
            </template>

            <template #[`item.application_id`]="{ item }">
              {{ item.application_id || "—" }}
            </template>

            <template #[`item.combined_tally.refunded`]="{ item }">
              {{ item.combined_tally.refunded }}
            </template>

            <template #[`item.combined_tally.total`]="{ item }">
              {{ Math.round(parseFloat(item.combined_tally.total)) }}
            </template>

            <template #[`item.combined_tally.allocated_to_invoices`]="{ item }">
              {{ item.combined_tally.allocated_to_invoices }}
            </template>

            <template #[`item.combined_tally.refunded`]="{ item }">
              {{ item.combined_tally.refunded }}
            </template>

            <template #[`item.combined_tally.remaining`]="{ item }">
              <span
                :class="
                  parseFloat(item.combined_tally.remaining) > 0
                    ? 'green--text font-weight-bold'
                    : 'grey--text'
                "
              >
                {{ Math.round(item.combined_tally.remaining) }}
              </span>
            </template>

            <template #[`item.actions`]="{ item }">
              <v-btn
                small
                class="accent white--text"
                @click="openEntriesDialog(item)"
              >
                View
              </v-btn>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Entries dialog -->
    <v-dialog v-model="entriesDialog" max-width="1000" scrollable>
      <v-card v-if="entriesDialog">
        <v-card-title class="font-weight-medium secondary--text primary">
          Excess Payments
          <v-spacer />
          <v-btn small icon @click="entriesDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <!-- Combined tally summary -->
        <v-card-text class="px-5 pt-4 pb-3">
          <v-row dense>
            <v-col cols="3" class="text-center">
              <div class="caption grey--text text--darken-1 mb-1">
                Total Excess
              </div>
              <div class="subtitle-2 font-weight-bold primary--text">
                ₹{{ selectedGroup.combined_tally.total }}
              </div>
            </v-col>
            <v-col cols="3" class="text-center">
              <div class="caption grey--text text--darken-1 mb-1">
                Applied to Invoices
              </div>
              <div class="subtitle-2 font-weight-bold blue--text">
                ₹{{ selectedGroup.combined_tally.allocated_to_invoices }}
              </div>
            </v-col>
            <v-col cols="3" class="text-center">
              <div class="caption grey--text text--darken-1 mb-1">Refunded</div>
              <div
                class="subtitle-2 font-weight-bold orange--text text--darken-1"
              >
                ₹{{ selectedGroup.combined_tally.refunded }}
              </div>
            </v-col>
            <v-col cols="3" class="text-center">
              <div class="caption grey--text text--darken-1 mb-1">
                Remaining
              </div>
              <div
                class="subtitle-2 font-weight-bold"
                :class="
                  parseFloat(selectedGroup.combined_tally.remaining) > 0
                    ? 'green--text text--darken-1'
                    : 'grey--text'
                "
              >
                ₹{{ selectedGroup.combined_tally.remaining }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <!-- Entry cards -->
        <v-card-text
          class="px-4 py-4"
          style="max-height: 460px; overflow-y: auto"
        >
          <v-row dense>
            <v-col
              v-for="entry in selectedGroup.entries"
              :key="entry.id"
              cols="12"
            >
              <v-card outlined class="rounded-lg">
                <v-card-text class="pa-4">
                  <v-row align="center" no-gutters>
                    <!-- Left: fee info -->
                    <v-col cols="12" md="3">
                      <div class="caption grey--text">Fee Type</div>
                      <div class="body-2 font-weight-medium d-flex align-center">
                        {{ entry.fee_type || "—" }}
                        <v-btn
                          v-if="entry.source === 'tuition_fee_excess'"
                          icon
                          x-small
                          color="primary"
                          class="ml-1"
                          @click="openRemarksDialog(entry)"
                        >
                          <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                      </div>
                      <div
                        v-if="entry.source !== 'tuition_fee_excess'"
                        class="caption grey--text mt-1"
                      >
                        {{ formatDate(entry.transaction_date) }}
                      </div>
                      <div
                        v-if="entry.receipt_number"
                        class="caption grey--text"
                      ></div>
                    </v-col>

                    <!-- Middle: tally -->
                    <v-col cols="12" md="6">
                      <v-row dense class="text-center">
                        <v-col cols="3">
                          <div class="caption grey--text">Amount</div>
                          <div class="body-2 font-weight-bold">
                            ₹{{ entry.amount }}
                          </div>
                        </v-col>
                        <v-col cols="3">
                          <div class="caption grey--text">Applied</div>
                          <div class="body-2 blue--text">
                            ₹{{ entry.tally.allocated_to_invoices }}
                          </div>
                        </v-col>
                        <v-col cols="3">
                          <div class="caption grey--text">Refunded</div>
                          <div class="body-2 orange--text text--darken-1">
                            ₹{{ entry.tally.refunded }}
                          </div>
                        </v-col>
                        <v-col cols="3">
                          <div class="caption grey--text">Remaining</div>
                          <div
                            class="body-2 font-weight-bold"
                            :class="
                              parseFloat(entry.tally.remaining) > 0
                                ? 'green--text text--darken-1'
                                : 'grey--text'
                            "
                          >
                            ₹{{ entry.tally.remaining }}
                          </div>
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Right: actions -->
                    <v-col
                      cols="12"
                      md="3"
                      class="d-flex justify-end align-center"
                      style="gap: 6px"
                    >
                      <v-btn
                        small
                        class="accent white--text"
                        :disabled="parseFloat(entry.tally.remaining) <= 0"
                        @click="
                          openAllocationDialog(entry, 'invoice_adjustment')
                        "
                      >
                        Invoice
                      </v-btn>
                      <v-btn
                        small
                        outlined
                        color="orange darken-1"
                        :disabled="parseFloat(entry.tally.remaining) <= 0"
                        @click="openAllocationDialog(entry, 'refund')"
                      >
                        Refund
                      </v-btn>
                      <v-btn
                        icon
                        small
                        color="primary"
                        @click="openHistoryDialog(entry)"
                      >
                        <v-icon small>mdi-history</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Remarks dialog -->
    <v-dialog v-model="remarksDialog" max-width="450">
      <v-card>
        <v-card-title class="subtitle-1 font-weight-bold">
          Remarks
          <v-spacer />
          <v-btn icon small @click="remarksDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 body-2" style="white-space: pre-line">
          {{ remarksText || "No remarks" }}
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Allocation dialog -->
    <v-dialog v-model="allocationDialog" max-width="600" persistent scrollable>
      <allocationDialogComponent
        v-if="allocationDialog"
        :excess-payment="selectedEntry"
        :mode="allocationMode"
        @close="allocationDialog = false"
        @saved="onAllocationSaved"
      />
    </v-dialog>

    <!-- History dialog -->
    <v-dialog v-model="historyDialog" max-width="680" scrollable>
      <v-card v-if="historyDialog">
        <v-card-title class="font-weight-medium secondary--text primary">
          Allocation History
          <v-spacer />
          <v-btn small icon @click="historyDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text
          class="px-4 py-4"
          style="max-height: 400px; overflow-y: auto"
        >
          <div v-if="historyLoading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <div
            v-else-if="!allocationHistory.length"
            class="text-center grey--text py-6 caption"
          >
            No allocations yet
          </div>

          <v-row v-else dense>
            <v-col v-for="item in allocationHistory" :key="item.id" cols="12">
              <v-card
                outlined
                class="rounded-lg"
                :class="item.status === 'cancelled' ? 'grey lighten-4' : ''"
              >
                <v-card-text class="pa-3">
                  <v-row align="center" no-gutters>
                    <!-- Type + status -->
                    <v-col cols="12" md="3">
                      <div class="d-flex align-center mb-1" style="gap: 6px">
                        <v-chip
                          x-small
                          :color="
                            item.usage_type === 'refund'
                              ? 'orange darken-1'
                              : 'blue darken-1'
                          "
                          :outlined="item.status === 'cancelled'"
                          dark
                        >
                          {{
                            item.usage_type === "refund" ? "Refund" : "Applied"
                          }}
                        </v-chip>
                        <v-chip
                          x-small
                          :color="
                            item.status === 'cancelled'
                              ? 'grey lighten-1'
                              : 'green darken-1'
                          "
                          dark
                        >
                          {{
                            item.status === "cancelled" ? "Cancelled" : "Active"
                          }}
                        </v-chip>
                      </div>
                      <div class="caption grey--text">
                        {{ formatDate(item.inserted_at) }}
                      </div>
                    </v-col>

                    <!-- Amount + details -->
                    <v-col cols="12" md="6">
                      <div class="d-flex flex-wrap" style="gap: 16px">
                        <div>
                          <div class="caption grey--text">Amount</div>
                          <div
                            class="body-2 font-weight-bold"
                            :class="
                              item.status === 'cancelled'
                                ? 'grey--text text-decoration-line-through'
                                : ''
                            "
                          >
                            ₹{{ item.amount }}
                          </div>
                        </div>

                        <!-- Refund details -->
                        <template v-if="item.usage_type === 'refund'">
                          <div>
                            <div class="caption grey--text">Bank</div>
                            <div class="body-2">{{ item.bank_name }}</div>
                          </div>
                          <div>
                            <div class="caption grey--text">Account</div>
                            <div class="body-2">{{ item.account_number }}</div>
                          </div>
                          <div>
                            <div class="caption grey--text">IFSC</div>
                            <div class="body-2">{{ item.ifsc_code }}</div>
                          </div>
                          <div v-if="item.refund_transaction_id">
                            <div class="caption grey--text">Txn ID</div>
                            <div class="body-2">
                              {{ item.refund_transaction_id }}
                            </div>
                          </div>
                        </template>

                        <div v-if="item.remarks">
                          <div class="caption grey--text">Remarks</div>
                          <div class="body-2">{{ item.remarks }}</div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Cancel action -->
                    <v-col cols="12" md="3" class="d-flex justify-end">
                      <v-btn
                        x-small
                        outlined
                        color="red darken-1"
                        :loading="deletingId === item.id"
                        :disabled="item.status === 'cancelled'"
                        @click="cancelAllocation(item)"
                      >
                        <v-icon x-small left>mdi-cancel</v-icon>
                        Cancel
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import allocationDialogComponent from "@/components/excessPayment/allocationDialog.vue";

export default {
  components: { allocationDialogComponent },

  data() {
    return {
      form: {},
      batches: [],
      loading: false,
      downloadLoading: false,
      groups: [],
      remainingFilter: null,
      appliedRemainingFilter: null,
      remainingOptions: [
        { text: "Has Balance", value: "has_remaining" },
        { text: "Fully Settled", value: "exhausted" },
      ],
      selectedGroup: null,
      selectedEntry: null,
      allocationMode: "refund",
      entriesDialog: false,
      allocationDialog: false,
      historyDialog: false,
      remarksDialog: false,
      remarksText: "",
      historyLoading: false,
      allocationHistory: [],
      historyTally: null,
      deletingId: null,

      groupHeaders: [
        { text: "Name", value: "name" },
        { text: "USN", value: "usn_id" },
        { text: "Application ID", value: "application_id" },
        { text: "Mobile", value: "mobile_no", sortable: false },
        { text: "Total (₹)", value: "combined_tally.total" },
        {
          text: "Applied (₹)",
          value: "combined_tally.allocated_to_invoices",
          sortable: false,
        },
        {
          text: "Refunded (₹)",
          value: "combined_tally.refunded",
          sortable: false,
        },
        { text: "Remaining (₹)", value: "combined_tally.remaining" },
        { text: "", value: "actions", sortable: false },
      ],
    };
  },

  computed: {
    filteredGroups() {
      if (!this.appliedRemainingFilter) return this.groups;
      return this.groups.filter((g) => {
        const rem = parseFloat(g.combined_tally.remaining);
        return this.appliedRemainingFilter === "has_remaining"
          ? rem > 0
          : rem === 0;
      });
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Excess Payments" }]);
    await this.generateBatchList();
    this.fetchExcessPayments();
  },

  methods: {
    async generateBatchList() {
      try {
        const res = await this.$axios.get("/get_year_batch");
        this.batches = res.data.data;
      } catch (e) {
        console.log(e);
      }
    },

    async fetchExcessPayments() {
      this.loading = true;
      this.appliedRemainingFilter = this.remainingFilter;
      try {
        const params = this.buildQuery({ ...this.form });
        const res = await this.$axios.get(`/excess_overview?${params}`);
        this.groups = res.data.data;
        this.groups.sort((a, b) => {
          return b.combined_tally?.remaining - a.combined_tally?.remaining;
        });
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    buildQuery(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      return Object.keys(obj)
        .filter((k) => obj[k] !== undefined && obj[k] !== null && obj[k] !== "")
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
        .join("&");
    },

    openEntriesDialog(group) {
      this.selectedGroup = group;
      this.entriesDialog = true;
    },

    openRemarksDialog(entry) {
      this.remarksText = entry.remarks;
      this.remarksDialog = true;
    },

    openAllocationDialog(entry, mode) {
      this.selectedEntry = entry;
      this.allocationMode = mode;
      this.allocationDialog = true;
    },

    async openHistoryDialog(entry) {
      this.selectedEntry = entry;
      this.historyTally = null;
      this.historyDialog = true;
      this.historyLoading = true;
      try {
        const base =
          entry.source === "tuition_fee_excess"
            ? "/tuition_fee_excess"
            : "/excess_payments";
        const res = await this.$axios.get(`${base}/${entry.id}/allocations`);
        const data = res.data.data;
        this.allocationHistory = data.allocations || [];
        this.historyTally = {
          total: data.total,
          allocated_to_invoices: data.allocated_to_invoices,
          refunded: data.refunded,
          remaining: data.remaining,
        };
      } catch (e) {
        console.log(e);
      } finally {
        this.historyLoading = false;
      }
    },

    async cancelAllocation(allocation) {
      this.deletingId = allocation.id;
      try {
        const base =
          this.selectedEntry?.source === "tuition_fee_excess"
            ? "/tuition_fee_excess_allocations"
            : "/excess_payment_allocations";
        await this.$axios.put(`${base}/${allocation.id}/cancel`);
        this.$store.commit("showSnackbar", {
          message: "Allocation cancelled",
          color: "success",
        });
        await this.openHistoryDialog(this.selectedEntry);
        this.fetchExcessPayments();
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Failed to cancel allocation";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.deletingId = null;
      }
    },

    onAllocationSaved() {
      this.allocationDialog = false;
      this.fetchExcessPayments().then(() => {
        if (this.selectedGroup) {
          const refreshed = this.groups.find(
            (g) => g.student_key === this.selectedGroup.student_key,
          );
          if (refreshed) this.selectedGroup = refreshed;
        }
      });
    },

    formatDate(dt) {
      if (!dt) return "—";
      const d = new Date(dt);
      if (isNaN(d)) return dt;
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    async downloadExcessReport() {
      this.downloadLoading = true;
      try {
        const params = this.buildQuery({ ...this.form });
        const res = await this.$axios.get(
          `/download_excess_payments?${params}`,
          {
            responseType: "blob",
          },
        );
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Excess Payments Report.csv");
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (e) {
        console.log(e);
      } finally {
        this.downloadLoading = false;
      }
    },
  },
};
</script>
